import React, { useState } from "react";

const quickLinks = [
  { title: "MLS Property Search", href: "https://marismls.com/", description: "Search available listings in St. Louis and surrounding Missouri counties." },
  { title: "Rental Application (PDF)", href: "/documents/Rental_Application.pdf", description: "Download or view the rental application form." },
  { title: "DocuSeal — Sign Documents Online", href: "https://docuseal-railway-production-4d4b.up.railway.app/", description: "Review and sign documents digitally (e.g. rental applications) from any device." },
  { title: "Contact", href: "#contact", description: "Get in touch with Patrick Driscoll for real estate services." },
];

const kirkwoodLinks = [
  { title: "Kirkwood City Tax Information", href: "https://www.kirkwoodmo.org/business/city-of-kirkwood-tax-rates", description: "City tax rates including property, sales, and utility taxes." },
  { title: "Kirkwood Online Utility Billing", href: "https://www.kirkwoodmo.org/government/departments/finance/online-utility-billing", description: "Manage and pay Kirkwood water and electric bills online." },
  { title: "Kirkwood Building Division", href: "https://www.kirkwoodmo.org/government/departments/building-division", description: "Building permits, occupancy inspections, and renovation regulations." },
  { title: "Kirkwood Zoning & City Maps", href: "https://www.kirkwoodmo.org/services/city-maps", description: "Ward maps, zoning designations, and park locations." },
  { title: "Kirkwood Municipal Court", href: "https://www.kirkwoodmo.org/government/departments/municipal-court", description: "Court dates and online payment for city fines or fees." },
  { title: "Kirkwood Parks & Recreation", href: "https://www.kirkwoodparksandrec.org/", description: "Local activities, facility rentals, and seasonal programs." },
];

const occupancyLinks = [
  { title: "City of Kirkwood Occupancy Portal", href: "https://www.kirkwoodmo.org/government/departments/building-division/occupancy-and-utilities-sign-up", description: "Kirkwood requires an inspection and new occupancy permit when a property is sold or a new tenant moves in. Apply online and schedule your inspection." },
  { title: "St. Louis County (Unincorporated) Occupancy", href: "https://stlouiscountymo.gov/st-louis-county-departments/transportation-and-public-works/occupancy-permit-applications/", description: "For properties in unincorporated St. Louis County. Apply for residential and commercial occupancy permits." },
  { title: "St. Louis City Housing Conservation", href: "https://www.stlouis-mo.gov/government/departments/public-safety/building/housing-conservation.cfm", description: "Housing Conservation inspections; most residential sales in the City require this certificate before the new owner moves in." },
  { title: "St. Louis County Municipal Matrix", href: "https://stlouiscountymo.gov/st-louis-county-departments/transportation-and-public-works/public-works-publications-and-manuals/matrix-information/", description: "County reference showing which municipalities handle their own inspections vs. contract with St. Louis County." },
];

const CATEGORIES = [
  {
    id: "quick",
    title: "Quick Links",
    subtitle: "Listings, applications, and contact",
    icon: "◆",
    items: quickLinks,
  },
  {
    id: "kirkwood",
    title: "Kirkwood City Resources",
    subtitle: "Tax, utilities, permits, parks & more",
    icon: "◇",
    note: "Driscoll Co. is in the heart of Kirkwood. Property assessment and real estate taxes: County Assessor · Collector of Revenue (St. Louis County).",
    items: kirkwoodLinks,
  },
  {
    id: "occupancy",
    title: "Occupancy & Inspection",
    subtitle: "Permits and inspections by location",
    icon: "▸",
    note: "Required when a property is sold or a new tenant moves in. Use the portal for your property’s location.",
    items: occupancyLinks,
  },
];

const LinkList = ({ items }) => (
  <ul className="divide-y divide-gray-100 text-left">
    {items.map(({ title, href, description }) => {
      const isExternal = href.startsWith("http");
      return (
        <li key={title}>
          <a
            href={href}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
            className="block py-3 sm:py-4 first:pt-0 last:pb-0 group transition-colors min-h-[44px] flex flex-col justify-center touch-manipulation"
          >
            <span className="font-semibold text-gray-900 group-hover:text-red-600 transition-colors text-[15px] sm:text-base inline-flex items-center gap-1.5">
              {title}
              {isExternal && <span className="text-gray-400 text-xs font-normal" aria-hidden title="Opens in new tab">↗</span>}
            </span>
            <span className="block text-sm text-gray-500 mt-1 leading-relaxed">{description}</span>
          </a>
        </li>
      );
    })}
  </ul>
);

function LinksModal({ category, onClose }) {
  if (!category) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="links-modal-title"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] sm:max-h-[90vh] flex flex-col overflow-hidden my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 sm:gap-4 p-4 sm:p-6 pb-4 border-b border-gray-100 flex-shrink-0">
          <div className="min-w-0 flex-1">
            <h2 id="links-modal-title" className="text-lg sm:text-xl font-bold text-gray-900 tracking-tight">
              {category.title}
            </h2>
            <p className="text-sm text-gray-500 mt-1">{category.subtitle}</p>
            {category.note && (
              <p className="text-sm text-gray-600 mt-3 leading-relaxed">{category.note}</p>
            )}
            {category.id === "kirkwood" && (
              <p className="text-sm mt-2">
                <a href="https://stlouiscountymo.gov/st-louis-county-government/county-assessor/" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-700 font-medium underline underline-offset-2 py-1 touch-manipulation">County Assessor</a>
                {" · "}
                <a href="https://stlouiscountymo.gov/st-louis-county-departments/revenue/collector-of-revenue" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-700 font-medium underline underline-offset-2 py-1 touch-manipulation">Collector of Revenue</a>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 min-w-[44px] min-h-[44px] rounded-full flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors touch-manipulation"
            aria-label="Close"
          >
            <span className="text-2xl leading-none">&times;</span>
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 sm:p-6 pt-4" style={{ WebkitOverflowScrolling: "touch" }}>
          <LinkList items={category.items} />
        </div>
      </div>
    </div>
  );
}

const Links = () => {
  const [activeCategory, setActiveCategory] = useState(null);

  return (
    <section id="links" className="w-full min-w-full h-full flex-shrink-0 snap-start flex items-center justify-center bg-gray-100 p-4 sm:p-6 md:p-8 overflow-y-auto">
      <div className="text-center max-w-4xl w-full bg-white rounded-lg shadow-xl p-4 sm:p-6 md:p-8 my-4 sm:my-0">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">Links</h1>
        <h2 className="text-xl sm:text-2xl md:text-3xl mb-6 sm:mb-8">St. Louis and Surrounding Missouri Counties</h2>
        <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-8">
          Property search, applications, document signing, and official resources for residents and property owners.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 text-left">
          {CATEGORIES.map((cat) => (
            <button
              type="button"
              key={cat.id}
              onClick={() => setActiveCategory(cat)}
              className="group rounded-lg bg-gray-50 border border-gray-200 p-5 sm:p-6 transition-all hover:bg-red-50 hover:border-red-200 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:ring-offset-2 touch-manipulation min-h-[120px] sm:min-h-0 text-left"
            >
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-red-600 text-white text-lg font-medium mb-3 group-hover:bg-red-700 transition-colors flex-shrink-0">
                {cat.icon}
              </span>
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1 group-hover:text-red-600 transition-colors">
                {cat.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">{cat.subtitle}</p>
              <p className="text-xs text-gray-500 mt-2">
                {cat.items.length} link{cat.items.length !== 1 ? "s" : ""}
              </p>
            </button>
          ))}
        </div>
      </div>

      <LinksModal category={activeCategory} onClose={() => setActiveCategory(null)} />
    </section>
  );
};

export default Links;
